"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RecoveryPhraseConfirm } from "./recovery-phrase-confirm";
import { RecoveryPhraseDisplay } from "./recovery-phrase-display";
import { checkChallengeAnswers, normalizeRecoveryWords, selectChallengePositions } from "./recovery-phrase-model";
export interface RecoveryPhraseSetupProps {
  words: readonly string[];
  onConfirmed: () => void;
  onCancel: () => void;
  pending?: boolean;
  backupSaved?: boolean;
  error?: string;
}

/** Receives one prepared phrase. Never generates, stores, logs or transmits it. */
export function RecoveryPhraseSetup(t0) {
  const $ = _c(30);
  const {
    words,
    onConfirmed,
    onCancel,
    pending: t1,
    backupSaved: t2,
    error
  } = t0;
  const pending = t1 === undefined ? false : t1;
  const backupSaved = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== words) {
    t3 = words.join(" ");
    $[0] = words;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const source = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== source) {
    t6 = {
      source,
      visible: false,
      seen: false,
      positions: t4 as number[],
      answers: t5 as Record<number, string>,
      invalid: false
    };
    $[4] = source;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [state, setState] = useState(t6);
  if (state.source !== source) {
    let t7;
    let t8;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = [];
      t8 = {};
      $[6] = t7;
      $[7] = t8;
    } else {
      t7 = $[6];
      t8 = $[7];
    }
    setState({
      source,
      visible: false,
      seen: false,
      positions: t7,
      answers: t8,
      invalid: false
    });
  }
  const challenge = state.positions.length > 0;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="font-semibold text-xl">Save your recovery phrase</h2>;
    t8 = <p className="text-muted-foreground text-sm">These words restore the master used to create your Sigma profiles. Keep them private and in order.</p>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] !== challenge || $[11] !== onConfirmed || $[12] !== pending || $[13] !== state || $[14] !== words) {
    t9 = challenge ? <RecoveryPhraseConfirm answers={state.answers} invalid={state.invalid} onAnswer={(position, value) => setState({
      ...state,
      invalid: false,
      answers: {
        ...state.answers,
        [position]: normalizeRecoveryWords(value)
      }
    })} onBack={() => setState({
      ...state,
      positions: [],
      answers: {},
      invalid: false,
      visible: false
    })} onCheck={() => {
      if (!checkChallengeAnswers(words, state.positions, state.answers)) {
        setState({
          ...state,
          invalid: true
        });
        return false;
      }
      onConfirmed();
      return true;
    }} pending={pending} positions={state.positions} /> : <><RecoveryPhraseDisplay visible={state.visible} words={words} /><div className="flex flex-wrap gap-3"><button aria-expanded={state.visible} className="min-h-11 rounded-md border px-4" disabled={pending} onClick={() => setState({
          ...state,
          visible: !state.visible,
          seen: true
        })} type="button">{state.visible ? "Hide phrase" : "Show phrase"}</button><button className="min-h-11 rounded-md bg-primary px-4 text-primary-foreground" disabled={pending || !state.seen} onClick={() => setState({
          ...state,
          visible: false,
          positions: selectChallengePositions(words.length),
          answers: {},
          invalid: false
        })} type="button">I saved these words</button></div></>;
    $[10] = challenge;
    $[11] = onConfirmed;
    $[12] = pending;
    $[13] = state;
    $[14] = words;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== backupSaved) {
    t10 = backupSaved && <p className="text-sm">Your encrypted backup is saved. Retry to finish setting up this same identity.</p>;
    $[16] = backupSaved;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== error) {
    t11 = error && <p className="text-destructive text-sm" role="alert">{error}</p>;
    $[18] = error;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== backupSaved || $[21] !== onCancel || $[22] !== pending || $[23] !== source) {
    t12 = !backupSaved && <button className="min-h-11 rounded-md border px-4" disabled={pending} onClick={() => {
      setState({
        source,
        visible: false,
        seen: false,
        positions: [],
        answers: {},
        invalid: false
      });
      onCancel();
    }} type="button">Cancel</button>;
    $[20] = backupSaved;
    $[21] = onCancel;
    $[22] = pending;
    $[23] = source;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t9) {
    t13 = <section aria-label="Save your recovery phrase" className="space-y-4">{t7}{t8}{t9}{t10}{t11}{t12}</section>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}